import { useState } from "react";
import { Box, styled, Typography, Tooltip } from "@mui/material";
import { StatusCard, IssueIndicator } from "../../components-new/common";
import { CalendarIcon, InfoIcon, TimeIcon } from "../../components-new/icons";
import ChromeIcon from "../../components-new/icons/ChromeIcon";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import LabelIcon from "@mui/icons-material/Label";
import InsightsIcon from "@mui/icons-material/Insights";
import DuoOutlinedIcon from "@mui/icons-material/DuoOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import CompareRoundedIcon from "@mui/icons-material/CompareRounded";
import { AdjustOutlined } from "@mui/icons-material";
import { API_BASE_PATH, API_BASE_RELATIVE_PATH } from "../../Config/Config";
import { useSelector } from "react-redux";
import { formatDuration } from "../../../src/utils/timeFormatter.js";
import RuleIcon from "@mui/icons-material/Rule";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AnalyzeDialog from "./AnalyzeDialog";
import VideoPopup from "../../components/RecordingTable/VideoPopup.jsx";
import { openAuthedResource } from "../../utils/authedResource";
import { useNotification } from "../../hooks";

const StatusContainer = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  border: 1px solid #eeee;
  padding: 15px;
  background: #ffff;
`;

const StatusBar = ({ testcaseid, testcaseresultId, data, onViewDivergence = null }) => {
  const userDetails = localStorage.getItem("userDetail");
  if (!userDetails) throw new Error("No user details found in localStorage");
  const user = JSON.parse(userDetails);

  const isMobile = useSelector((state) => state.authoringSlice.isMobile);

  const notify = useNotification();

  // Manifest request for the recording. The credential travels in the
  // Authorization header — both for this call and for the per-video URLs it
  // returns, which VideoPopup fetches the same way.
  const recordingUrl = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=downloadTCRvideo&companyId=${user.companyid}&testcaseid=${testcaseid}&testcaseresultId=${testcaseresultId}`;

  const isMultiRun = !!data?.multiRun;
  const hasDesignPrincipals = !!data?.designPrincipals;

  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const handleAnalyzeOpen = () => setAnalyzeOpen(true);
  const handleAnalyzeClose = () => setAnalyzeOpen(false);

  const [videoOpen, setVideoOpen] = useState(false);
  const handleVideoOpen = () => setVideoOpen(true);
  const handleVideoClose = () => setVideoOpen(false);

  const handleOpenLogFile = () => {
    openAuthedResource(data.logFile, {
      filename: `${data?.testcasename || "test-case"}.log`,
    }).catch((error) => notify(error.message || "Failed to open the log file.", "error"));
  };

  const handleViewBenchmark = (mode) => {
    const runDate = data?.Executed_Date || "";
    const tcname = data?.testcasename || "";
    const url = `/new/reports/benchmark?tcrid=${testcaseresultId}&tcid=${testcaseid}&tcname=${encodeURIComponent(tcname)}&date=${encodeURIComponent(runDate)}&mode=${mode}`;
    window.open(url, "_blank");
  };

  return (
    <StatusContainer>
      <Box display="flex" columnGap={5} flex={2}>
        <StatusCard icon={<CalendarIcon />} title="Run Date" value={data?.Executed_Date} />

        <Tooltip title={data?.testcasename || ""} placement="top" arrow>
          <Box sx={{ maxWidth: 160 }}>
            <StatusCard
              icon={<LabelIcon style={{ color: "#FF5336" }} />}
              title="Name"
              value={
                <Typography noWrap sx={{ fontSize: "14px", maxWidth: 130 }}>
                  {data?.testcasename}
                </Typography>
              }
            />
          </Box>
        </Tooltip>

        <StatusCard icon={<TimeIcon />} title="Run Time" value={formatDuration(data?.Duration)} />

        <StatusCard
          icon={<AdjustOutlined style={{ color: "#FF5336" }} />}
          title="Run Mode"
          value={data?.runMode || "N/A"}
          tooltip={
            <Box>
              <Typography fontSize="12px" fontWeight={600} mb={0.5}>
                Test Execution Mode
              </Typography>
              <Typography fontSize="11px">
                • <b>Normal</b> — Single standard run
              </Typography>
              <Typography fontSize="11px">
                • <b>Multi Run</b> — Executed multiple times
              </Typography>
              <Typography fontSize="11px">
                • <b>Baseline</b> — Used as a reference run
              </Typography>
            </Box>
          }
        />

        <IssueIndicator
          status={data?.Status}
          issues={data?.issues}
          issueType={data?.issuetype}
          divergentDetails={data?.divergentDetails}
          onViewDivergence={onViewDivergence}
        >
          <StatusCard
            icon={<InfoIcon />}
            title="Status"
            valueColor={data?.Status === "PASS" ? "green" : "red"}
            value={data?.Status}
          />
        </IssueIndicator>

        <StatusCard
          icon={<AutoFixHighIcon style={{ color: "#FF5336" }} />}
          title="Self Healed"
          value={data?.self_healed_count || "N/A"}
        />

        <StatusCard
          icon={<RuleIcon style={{ color: "#FF5336" }} />}
          title="Assertion"
          value={
            <Box display="flex" alignItems="center" columnGap={0.5}>
              <Typography sx={{ fontSize: "14px" }}>
                {data?.Total_Assertion != null
                  ? `${data?.Assertion_Passed ?? 0}/${data?.Total_Assertion}`
                  : "N/A"}
              </Typography>
              <Tooltip title="Executed Assertions / Total Assertions" arrow>
                <Box display="flex" sx={{ cursor: "pointer" }}>
                  <InfoIcon />
                </Box>
              </Tooltip>
            </Box>
          }
        />

        <StatusCard
          icon={<InsightsIcon style={{ color: "#FF5336" }} />}
          title="Predictive Score"
          value={data?.confidence_score ?? "N/A"}
        />

        {!isMobile && <StatusCard icon={<ChromeIcon />} title="Browser" value={data?.Browser} />}

        <StatusCard
          icon={<FilePresentIcon style={{ color: "#FF5336" }} />}
          title="Log File"
          value={
            data?.logFile ? (
              <Typography
                onClick={handleOpenLogFile}
                sx={{
                  fontSize: "14px",
                  cursor: "pointer",
                  color: "#2550d1",
                  textDecoration: "underline",
                }}
              >
                Log File
              </Typography>
            ) : (
              <Typography sx={{ fontSize: "14px" }}>N/A</Typography>
            )
          }
        />

        <StatusCard
          icon={<DuoOutlinedIcon style={{ color: "#FF5336" }} />}
          title="Recording"
          value={
            <Typography
              onClick={handleVideoOpen}
              sx={{
                fontSize: "14px",
                cursor: "pointer",
                color: "#2550d1",
                textDecoration: "underline",
              }}
            >
              Recording
            </Typography>
          }
        />

        {isMultiRun && (
          <StatusCard
            icon={<CompareRoundedIcon style={{ color: "#FF5336" }} />}
            title="Multi-Run"
            value={
              <Typography
                onClick={() => handleViewBenchmark("multirun")}
                sx={{
                  fontSize: "14px",
                  cursor: "pointer",
                  color: "#2550d1",
                  textDecoration: "underline",
                }}
              >
                View Results
              </Typography>
            }
          />
        )}

        {hasDesignPrincipals && (
          <StatusCard
            icon={<CompareRoundedIcon style={{ color: "#FF5336" }} />}
            title="Design Principals"
            value={
              <Typography
                onClick={() =>
                  handleViewBenchmark(
                    data?.principleLevel === "Test_step"
                      ? "stepdesignprincipals"
                      : "designprincipals",
                  )
                }
                sx={{
                  fontSize: "14px",
                  cursor: "pointer",
                  color: "#2550d1",
                  textDecoration: "underline",
                }}
              >
                View Results
              </Typography>
            }
          />
        )}

        <StatusCard
          icon={<AnalyticsIcon style={{ color: "#FF5336" }} />}
          title="Analyze Report"
          value={
            <Typography
              onClick={handleAnalyzeOpen}
              sx={{
                fontSize: "14px",
                cursor: "pointer",
                color: "#2550d1",
                textDecoration: "underline",
              }}
            >
              Analyze
            </Typography>
          }
        />
      </Box>

      <AnalyzeDialog
        open={analyzeOpen}
        onClose={handleAnalyzeClose}
        testcaseid={testcaseid}
        testcasename={data?.testcasename}
      />

      <VideoPopup
        videoUrl={recordingUrl}
        open={videoOpen}
        onClose={handleVideoClose}
        tcnameForvideo={data?.testcasename}
      />
    </StatusContainer>
  );
};

export default StatusBar;
